/**
 * Centralized Logger Utility
 * 
 * In production: Only logs warn and error
 * In development: Logs all levels (debug, info, warn, error)
 * 
 * Usage:
 *   import logger from './logger';
 *   logger.debug('Debug message', data);
 *   logger.info('Info message', data);
 *   logger.warn('Warning message', data);
 *   logger.error('Error message', error);
 */

const isDev = process.env.NODE_ENV === 'development';

const formatMessage = (level, ...args) => {
  const timestamp = new Date().toISOString();
  return [`[${timestamp}] [${level}]`, ...args];
};

const logger = {
  /**
   * Debug level - only shown in development
   * Use for detailed debugging information
   */
  debug: (...args) => {
    if (isDev) {
      console.log(...formatMessage('DEBUG', ...args));
    }
  },

  /**
   * Info level - only shown in development
   * Use for general informational messages
   */
  info: (...args) => {
    if (isDev) {
      console.info(...formatMessage('INFO', ...args));
    }
  },

  /**
   * Warn level - shown in all environments
   * Use for warnings that don't break functionality
   */
  warn: (...args) => {
    console.warn(...formatMessage('WARN', ...args));
  },

  /**
   * Error level - shown in all environments
   * Use for errors that may affect functionality
   */
  error: (...args) => {
    console.error(...formatMessage('ERROR', ...args));
  },

  /**
   * Group logs together (dev only)
   */
  group: (label) => {
    if (isDev) {
      console.group(label);
    }
  },

  /**
   * End log group (dev only)
   */
  groupEnd: () => {
    if (isDev) {
      console.groupEnd();
    }
  },
};

export default logger;
